import React, { useState } from 'react'
import * as selector from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { Autocomplete, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { CancelIcon, HidePassword, LockGrayIcon, PaymentCopyLinkIcon, ShowPassword, SucessOutlineIcon, UnsubscribeIcon } from '../../icon';
import ButtonComponent from '../shared/button/button.component';
import { buttonLabels, ToasterTexts } from '../shared/constants';
import { ApiAccessService } from '../../services/apiAccessService';
import { PhoneNumberModel } from '../../models/phone-number.model';
import { UtilityService } from '../../services/UtilityService';
import { ApiAccessModel, mapToApiAccessModel } from '../../models/ApiAccess.model';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';

interface Props {
    OnClose: (isConfirm: boolean) => void;
    Show: boolean;
    ApiAccess: ApiAccessModel;
}

const APIAccessViewComponent = (props: Props) => {
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)
    const PhoneNumbersData: PhoneNumberModel[] = useSelector(selector.phoneNumbers)
    const IsTeamsApp: boolean = useSelector(selector.isTeamsApp)

    const [apiAccess, setApiAccess] = useState<{ Id: string, Name: string, FromPhonenumbers: string[], WebhookUrl: string, Scopes?: string[] }>(
        {
            ...(props.ApiAccess || {
                Id: "",
                Name: "",
                FromPhonenumbers: [],
                WebhookUrl: "",
                Scopes: []
            })
        })
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [errors, setErrors] = useState<{ Name: string, FromPhonenumbers: string, WebhookUrl: string }>({ Name: "", FromPhonenumbers: "", WebhookUrl: "" })
    const [showResponseData, setShowResponseData] = useState<boolean>(false)
    const [responseData, setResponseData] = useState<ApiAccessModel>()
    const [checked, setChecked] = useState<boolean>(false)
    const [showSecret, setShowSecret] = useState<boolean>(false)
    const [showWebhookSecret, setShowWebhookSecret] = useState<boolean>(false)

    const onNameChange = (value: string) => {
        setApiAccess({ ...apiAccess, Name: value })
        if (value === "" || value?.length === 0) {
            setErrors({ ...errors, Name: "Name cannot be blank" })
        }
        else if (value?.length > 30) {
            setErrors({ ...errors, Name: "Name should not exceed 30 characters" })
        }
        else {
            setErrors({ ...errors, Name: "" })
        }
    }

    const onFromNumberChanges = (event: any, value: PhoneNumberModel[]) => {
        setApiAccess({ ...apiAccess, FromPhonenumbers: value?.map((x) => x.PhoneNumber) ?? [] })
        if (value?.length === 0) {
            setErrors({ ...errors, FromPhonenumbers: "From numbers cannot be blank" })
        }
        else {
            setErrors({ ...errors, FromPhonenumbers: "" })
        }
    }

    const onWebhookUrlChange = (value: string) => {
        setApiAccess({ ...apiAccess, WebhookUrl: value })
        if (value === "" || value?.length === 0) {
            setErrors({ ...errors, WebhookUrl: "Webhook url cannot be blank" })
        }
        else if (value && !UtilityService.isValidUrl(value)) {
            setErrors({ ...errors, WebhookUrl: "Please enter valid url" })
        }
        else {
            setErrors({ ...errors, WebhookUrl: "" })
        }
    }

    const copySecret = (code: string, type: string) => {
        if (IsTeamsApp) {
            UtilityService.copyTextFromTeams(code)
            setToast(ToasterTexts.LINK_HAS_BEEN_COPIED?.replace("Link", type), ToasterType.SUCCESS)
        }
        else {
            navigator.clipboard.writeText(code);
            setToast(ToasterTexts.LINK_HAS_BEEN_COPIED.replace("Link", type), ToasterType.SUCCESS)
        }
    }

    const isValid = () => {
        return Object.values(errors).every(value => value === "") && !!apiAccess?.Name && !!apiAccess?.WebhookUrl && !!apiAccess?.FromPhonenumbers?.length
    }

    const saveApiAccess = () => {
        const data = {
            id: !!apiAccess?.Id ? apiAccess?.Id : "",
            name: apiAccess.Name,
            allowedPhoneNumbers: apiAccess.FromPhonenumbers,
            webhookUrl: apiAccess.WebhookUrl,
        }
        setIsLoading(true)
        if (!!apiAccess?.Id) {
            ApiAccessService.updateApiAccess(data).then((res) => {
                props.OnClose(true)
                setToast(ToasterTexts.APIACCESS_UPDATED, ToasterType.SUCCESS)
                setIsLoading(false)
            }).catch((err) => {
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
                setIsLoading(false)
            })
        } else {
            ApiAccessService.createApiAccess(data).then((res) => {
                setToast(ToasterTexts.APIACCESS_CREATED, ToasterType.SUCCESS)
                setResponseData(mapToApiAccessModel(res.data))
                setShowResponseData(true)
                setIsLoading(false)
            }).catch((err) => {
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
                setIsLoading(false)
            })
        }
    }


    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            BackdropProps={{ style: { backgroundColor: "transparent" } }}
            open={props.Show}
            onClose={() => { !showResponseData && props.OnClose(false) }}
            classes={{ paper: `br-2 w-p-36 ${IsDarkMode ? "" : "dialogue-shadow"} ` }}
            disableEscapeKeyDown
        >
            {!showResponseData && <DialogTitle className={`pb-1 pt-2 px-4 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"} bb-primary-divider-4`}>
                <div className={`d-flex justify-between align-center ${IsDarkMode ? "" : "color-settings-heading"} h3-semibold`}><div className='d-flex flex-direction'>
                    <span>
                        {"Create Api Access"}
                    </span>
                    <span className='color-text-field-font h5-regular'>
                        Scoped to messages:send for 1-to-1 text delivery.
                    </span>
                </div>

                    <CancelIcon onClick={() => { props.OnClose(false) }} className="c-pointer" />
                </div>
            </DialogTitle>}
            {!showResponseData ?
                <DialogContent className={`px-4 py-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"}`}>
                    <div className='h4-medium color-primary-accent'>Name *</div>
                    <TextField
                        fullWidth
                        size='small'
                        placeholder='enter text'
                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                        autoComplete='no'
                        onChange={(e) => { onNameChange(e.target?.value) }}
                        value={apiAccess.Name}
                    />
                    <div className='color-primary-danger h6-regular float-right h-3'>{errors?.Name}</div>

                    <div className='h4-medium color-primary-accent mt-1'>Allowed from-numbers *</div>
                    <Autocomplete
                        multiple
                        renderInput={params => (
                            <TextField
                                size='small'
                                {...params}
                                label=""
                                variant='outlined'
                                placeholder={!!apiAccess?.FromPhonenumbers?.length ? "" : "Select activated numbers "}
                                inputProps={{ ...params.inputProps, autoComplete: "off", }}
                                InputProps={{
                                    ...params.InputProps,
                                    classes: {
                                        input: "h4-regular color-text-field-font",
                                    },
                                }}
                            />
                        )}
                        getOptionLabel={(option) => UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber) + ` (${option?.Name})`}
                        options={PhoneNumbersData}
                        onChange={(event: any, value: any) => { onFromNumberChanges(event, value) }}
                        renderOption={(defaultprops, option: any) => (
                            <li {...defaultprops}>
                                <span className={"c-pointer h4-regular color-menu-list-font-color p-1 d-flex justify-between w-p-92"}>
                                    <div >{option?.Name} </div>
                                    <div> {UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber)}</div>
                                </span>
                            </li>
                        )}
                        value={PhoneNumbersData?.filter(x => apiAccess?.FromPhonenumbers?.includes(x.PhoneNumber))}
                    />
                    <div className='color-primary-danger h6-regular float-right h-3'>{errors?.FromPhonenumbers}</div>

                    <div className='h5-regular color-text-field-font mt-1'>Only numbers activated for your organization can send. Leave empty to allow all activated numbers.</div>
                    <div className='h4-medium color-primary-accent mt-1'>Webhook URL</div>
                    <TextField
                        fullWidth
                        size='small'
                        placeholder='enter text'
                        InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                        autoComplete='no'
                        value={apiAccess?.WebhookUrl}
                        onChange={(e) => { onWebhookUrlChange(e.target?.value) }}
                    />
                    <div className='color-primary-danger h6-regular float-right h-3'>{errors?.WebhookUrl}</div>

                    <div className='h5-regular color-text-field-font mt-1'>Delivery receipts are POSTed here. HTTPS and a publicly reachable host are required.</div>
                    <div className='h4-medium color-primary-accent mt-1'>Scopes</div>
                    <div className='d-flex align-center ml-0 bg-color-text-field-border w-20 mx-auto py-1 br-5 justify-center'>
                        <LockGrayIcon />
                        <div className='color-text-field-font h5-regular ml-1'>Messages : send</div>
                    </div>
                    <div className='h5-regular color-text-field-font mt-1'>Additional scopes will appear here as new APIs ship.</div>
                </DialogContent> :
                <DialogContent className={`px-4 py-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"} color-priamry-accent`}>
                    <div className='d-flex'>
                        <SucessOutlineIcon className='w-3 h-3' />
                        <div className='h3-semibold ml-1'>
                            <div>API key created</div>
                            <div className='h5-regular'>Copy the credentials below into your secret manager before closing.</div>
                        </div>
                    </div>

                    <div className='d-flex mt-2 p-1 br-2 bg-color-internal-note-wrap-bg b-around-internal-note-wrap-border color-lite-dark-black'>
                        <UnsubscribeIcon className='h-5 w-5' />
                        <div className='ml-1'  >
                            <span className="h5-semibold">Save these now. </span>
                            <span className="h5-regular">
                                The secret and signing secret cannot be retrieved again — you'll need to create a new key if lost.
                            </span>
                        </div>
                    </div>
                    <div className='d-flex justify-between mt-2'>
                        <span className='h4-semibold'>Key ID</span>
                        <span className='h5-regular'>Also shown in the keys list</span>
                    </div>

                    <div className='d-flex align-center justify-between p-1 border-primary-divider br-2  bg-color-con-hist-inbound-msg-bg'>
                        <TextField
                            variant='standard'
                            value={responseData?.Id}
                            fullWidth
                            disabled
                            className='mr-2'
                        />

                        <div className='border-primary-divider p-1 br-2 h5-regular d-flex align-center bg-color-secondary c-pointer' onClick={() => copySecret(responseData?.Id, "Key")}>
                            <PaymentCopyLinkIcon />
                            <span className='ml-1' >Copy</span>
                        </div>
                    </div>
                    <div className='h4-semibold mt-2'>Send Secret</div>
                    <div className='d-flex align-center justify-between p-1 border-primary-divider br-2 '>
                        <TextField
                            variant='standard'
                            value={responseData?.Secret}
                            fullWidth
                            disabled
                            type={showSecret ? 'text' : 'password'}
                            className='mr-2'
                        />
                        <div className='d-flex align-center '>
                            <div className=' border-primary-divider br-2 mr-2 bg-color-secondary w-4 h-4 d-flex align-center justify-center c-pointer' onClick={() => setShowSecret(!showSecret)}>
                                {showSecret ? <HidePassword /> : <ShowPassword />}
                            </div>
                            <div className='border-primary-divider p-1 br-2 h5-regular d-flex align-center bg-color-secondary c-pointer' onClick={() => copySecret(responseData?.Secret, "Secret")}>
                                <PaymentCopyLinkIcon />
                                <span className='ml-1' >Copy</span>
                            </div>
                        </div>
                    </div>

                    <div className='h4-semibold mt-2'>Webhook Signing Secret</div>
                    <div className='d-flex align-center justify-between p-1 border-primary-divider br-2 '>
                        <TextField
                            variant='standard'
                            value={responseData?.WebhookSigningSecret}
                            fullWidth
                            disabled
                            type={showWebhookSecret ? 'text' : 'password'}
                            className='mr-2'
                        />
                        <div className='d-flex align-center '>
                            <div className=' border-primary-divider br-2 mr-2 bg-color-secondary w-4 h-4 d-flex align-center justify-center c-pointer' onClick={() => setShowWebhookSecret(!showWebhookSecret)}>
                                {showWebhookSecret ? <HidePassword /> : <ShowPassword />}
                            </div>
                            <div className='border-primary-divider p-1 br-2 h5-regular d-flex align-center bg-color-secondary c-pointer' onClick={() => copySecret(responseData?.WebhookSigningSecret, "Webhook signing secret")}>
                                <PaymentCopyLinkIcon />
                                <span className='ml-1 '>Copy</span>
                            </div>
                        </div>
                    </div>
                    <span className='h5-regular color-text-field-label'>Use this to verify the signature on every webhook Falkon SMS sends you.</span>

                    <div className='d-flex align-center p-1 border-primary-divider br-2 mt-1'>
                        <Checkbox checked={checked}
                            classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                            onChange={(e) => setChecked(e.target.checked)}
                            className='p-1'
                        />
                        <div className='br-2 h4-regular'>I've saved these credentials</div>
                    </div>
                </DialogContent>
            }
            <DialogActions className={`pb-2 px-2 pt-2 brb-3 ${IsDarkMode ? " bg-color-sidebar-bg" : ""} bg-color-con-hist-inbound-msg-bg`}>
                {!showResponseData ?
                    <>
                        <ButtonComponent OnClick={() => props.OnClose(false)} ButtonLabel={buttonLabels.CANCEL} ClassName={""} Variant={"secondary-btn-small"} />
                        <ButtonComponent OnClick={() => { saveApiAccess() }} ButtonLabel={buttonLabels.SAVE} Loading={isLoading} ClassName={"px-4"} Variant={isValid() ? "primary-btn-small" : "disabled-btn-small"} />
                    </>
                    : <div className='d-flex justify-between align-center w-p-96 '>
                        <div className='h4-regular color-text-field-font'>Confirm you have saved the credentials to continue.</div>
                        <ButtonComponent OnClick={() => { props.OnClose(true) }} ButtonLabel={buttonLabels.DONE} Loading={isLoading} ClassName={"px-4"} Variant={checked ? "primary-btn-small" : "disabled-btn-small"} /></div>
                }
            </DialogActions>
        </Dialog>
    )
}

export default APIAccessViewComponent